import { ISectionLogger } from "@multiweb/logging";
import { DateUtils } from "../../../../Global/Base/Utils/DateUtils";
import { NumberUtils } from "../../../../Global/Base/Utils/NumberUtils";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { Control } from "../Controls/Control";
import { ControlWithLoggerParams, IAttrNameControl, IRequiresActualObject } from "../Controls/ControlInterfaces";
import { HasFrameContextMixin } from "../Controls/HasFrameContextMixin";
import { IContentBuilder } from "../DataBuilders/IContentBuilder";
import { LocalizedStringBuilder } from "../DataBuilders/LocalizedStringBuilder";
import { StringBuilder } from "../DataBuilders/StringBuilder";
import { MessageButtonType, MessageWindow } from "../MessageWindow";

export class InfoLabel extends HasFrameContextMixin(Control) implements IAttrNameControl, IRequiresActualObject {
    //#region Fields
    /**
     * Nazev atributu, pri jehoz zmene se zobrazuje okno s hlaskou
     */
    public attrName: string;

    private readonly _logger: ISectionLogger;
    private _actualObject: OWObject;

    /**
     * Kod lokalizacni hlasky, ktera se ma zobrazit
     */
    private readonly _captionLc: string;
    /**
     * Nazev atributu, do nehoz se ma nastavit vysledek dotazu na uzivatele -  ano/ne
     */
    private readonly _resultAttrName: string;
    /**
     * Nazev atributu, jehoz obsah se ma zobrazit lokalizacni hlasky
     */
    private readonly _captionAttr: string;
    //#endregion Fields

    //#region Properties
    public get actualObject(): OWObject {
        return this._actualObject;
    }

    public set actualObject(value: OWObject) {
        if (this.actualObject)
            this.actualObject.relationAttributeChanged.remove(this._attributeChangedEventMethod);

        this._actualObject = value;
        if (this.actualObject)
            this.actualObject.relationAttributeChanged.add(this._attributeChangedEventMethod);
    }

    //#endregion Properties

    //#region Constructors
    constructor({ logger, ...otherParams }: ControlWithLoggerParams) {
        super(otherParams);
        this._logger = logger;
        this.attrName = this.getDataAttr("attrname");

        this._captionLc = this.getDataAttr("lockey");
        if (this._captionLc && this._captionLc.endsWith("-666")) // nevyplneny lokalizacni klic - xgen tam automaticky nacpe -666 nebo XG-666
            this._captionLc = undefined;

        this._captionAttr = this.getDataAttr("captionAttr");
        this._resultAttrName = this.getDataAttr("resultattrname");

        if (!this._captionLc && !this._captionAttr)
            this._logger.error("One of LocKey or CaptionAttr must be set on InfoLabel!");
        else if (this._captionLc && this._captionAttr)
            this._logger.warn("Both LocKey or CaptionAttr attributes are set - only one should be used - using Lockey");
    }
    //#endregion Constructors

    //#region Methods

    /**
     * Zobrazeni zpravy po zmene atributu
     */
    private async _displayMessage(): Promise<void> {
        const captionBuilder = await this._getCaptionBuilder();
        if (!captionBuilder) // Nemáme captionBuilder - nebudeme zobrazovat okno
            return;
        await MessageWindow.showAlert(captionBuilder);
    }

    /**
     * Zobrazeni potvrzovaciho dialogu
     */
    private async _displayConfirmDialog(): Promise<void> {
        const captionBuilder = await this._getCaptionBuilder();
        if (!captionBuilder) // Nemáme captionBuilder - nebudeme zobrazovat okno
            return;
        const value = await MessageWindow.showYesNoDialog(captionBuilder);
        await this._applyDialogValue(value.result);
    }

    /**
     * Metoda volana po zmene AO. Pokud je zmeneny atribut na AO stejny jako nastaveny AttrName,
     * zobrazi se na zaklade property ResultAttrName dialog
     */
    private _attributeChangedEventMethod = async (_sender: OWObject, attributeName: string) => {
        if (attributeName === this.attrName) {
            if (this._resultAttrName)
                await this._displayConfirmDialog();
            else
                await this._displayMessage();
        }
    };

    /**
     * Nastavi hodnotu v dialogu (OK/NO) a do ActualObjectu
     * @param value zvolena hodnota z dialogu
     */
    private _applyDialogValue(value: MessageButtonType): Promise<void> {
        switch (value) {
            case MessageButtonType.ok:
                return this._setResultAttr(true);
            case MessageButtonType.no:
                return this._setResultAttr(false);
        }
        return Promise.resolve();
    }

    /**
     * Nastaveni atributu uvedeneho v ResultAttr hodnotu v parametru
     * @param value nastavovana hodnota
     */
    private async _setResultAttr(value: boolean): Promise<void> {
        await this.actualObject.setValue(this._resultAttrName, value);
    }

    private async _getCaptionBuilder(): Promise<IContentBuilder | null> {
        if (this._captionLc)
            return new LocalizedStringBuilder(this._captionLc);

        if (this._captionAttr) {
            const attrValue = await this.actualObject.getValue(this._captionAttr);
            if (!attrValue)
                this._logger.warn(`Attribute '${this._captionAttr}' specified in CaptionAttr has no content`);
            else if (typeof attrValue === "string")
                return new StringBuilder(attrValue);
            else if (typeof attrValue === "number")
                return new StringBuilder(NumberUtils.numberToString((attrValue as number).toString(), {}));
            else if (attrValue instanceof Date) {
                return new StringBuilder(DateUtils.dateToString(attrValue));
            }
        }

        // pokud by nahodu nebyl vyplnen ani jeden atribut nebo byla jeho hodnota prázdná
        this._logger.warn("Empty InfoLabel has not been shown.");
        return null;
    }

    //#endregion Methods

    //#region dispose
    public override dispose(): void {
        if (this.actualObject)
            this.actualObject.attributeChanged.remove(this._attributeChangedEventMethod);
    }

    //#endregion dispose
}
